import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Trophy } from "@phosphor-icons/react";

const TIME_LIMIT_MS = 10000;

export default function TriviaGame() {
    const { gameId } = useParams();
    const nav = useNavigate();
    const loc = useLocation();
    const [game, setGame] = useState(loc.state?.game || null);
    const [current, setCurrent] = useState(loc.state?.game?.first_question || null);
    const [score, setScore] = useState(0);
    const [questionStart, setQuestionStart] = useState(Date.now());
    const [feedback, setFeedback] = useState(null); // {correct, correct_index, points}
    const [answering, setAnswering] = useState(false);
    const [progress, setProgress] = useState(100);
    const [finalResult, setFinalResult] = useState(null);
    const intervalRef = useRef(null);

    // Load game if not provided via state
    useEffect(() => {
        if (!game) {
            api.get(`/trivia/games/${gameId}`).then(({ data }) => {
                if (data.finished) {
                    setFinalResult({ score: data.score, total: data.questions.length });
                } else {
                    const q = data.questions[data.current_idx];
                    setGame({ game_id: data.id, total_questions: data.questions.length });
                    setCurrent({ idx: data.current_idx, ...q });
                    setScore(data.score);
                    setQuestionStart(Date.now());
                }
            }).catch((e) => {
                toast.error(formatErr(e.response?.data?.detail));
                nav("/trivia");
            });
        }
    }, [gameId]);

    // Timer
    useEffect(() => {
        if (!current || feedback) return;
        setQuestionStart(Date.now());
        setProgress(100);
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - questionStartRef.current;
            const pct = Math.max(0, 100 - (elapsed / TIME_LIMIT_MS) * 100);
            setProgress(pct);
            if (elapsed >= TIME_LIMIT_MS) {
                clearInterval(intervalRef.current);
                onAnswer(-1, true);
            }
        }, 100);
        return () => clearInterval(intervalRef.current);
        // eslint-disable-next-line
    }, [current?.idx, feedback]);

    // Track question start in ref to use in interval
    const questionStartRef = useRef(questionStart);
    useEffect(() => { questionStartRef.current = questionStart; }, [questionStart]);

    const onAnswer = async (answerIdx, timeout = false) => {
        if (answering || feedback) return;
        setAnswering(true);
        clearInterval(intervalRef.current);
        const timeTaken = Date.now() - questionStart;
        try {
            const { data } = await api.post("/trivia/answer", {
                game_id: gameId,
                question_idx: current.idx,
                answer_idx: timeout ? -1 : answerIdx,
                time_taken_ms: Math.min(timeTaken, TIME_LIMIT_MS),
            });
            setFeedback({ correct: data.correct, correct_index: data.correct_index, points: data.points });
            setScore((s) => s + data.points);
            setTimeout(() => {
                if (data.finished) {
                    setFinalResult({ score: data.score, total: game.total_questions, prize: data.prize });
                } else {
                    setCurrent(data.next_question);
                    setFeedback(null);
                    setQuestionStart(Date.now());
                }
                setAnswering(false);
            }, 1300);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setAnswering(false);
        }
    };

    if (finalResult) {
        return (
            <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center">
                <Trophy size={80} weight="fill" className="text-wta-accent" />
                <div className="font-heading text-xs tracking-[0.4em] text-wta-muted mt-6">FINAL SCORE</div>
                <div className="font-heading text-7xl md:text-9xl tracking-tight">{finalResult.score}</div>
                {finalResult.prize > 0 && (
                    <div className="mt-4 text-wta-accent font-heading text-2xl">+ ${finalResult.prize.toFixed(2)} earned</div>
                )}
                <div className="mt-10 flex gap-3">
                    <button data-testid="trivia-play-again" onClick={() => nav("/trivia")} className="btn-primary px-6 py-3 rounded-sm text-base">Play again</button>
                    <button data-testid="trivia-leaderboard" onClick={() => nav("/leaderboard")} className="btn-ghost px-6 py-3 rounded-sm text-base font-heading uppercase tracking-wider">Leaderboard</button>
                </div>
            </div>
        );
    }

    if (!current) return <div className="p-10 text-wta-muted">Loading question…</div>;

    return (
        <div className="relative min-h-[100dvh] flex flex-col">
            <img src="https://images.unsplash.com/photo-1679855928057-b7c9b0776c83" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
            <div className="relative flex-1 flex flex-col p-6 md:p-12 max-w-3xl mx-auto w-full">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-6">
                    <div className="font-heading text-sm tracking-widest text-wta-muted">QUESTION {current.idx + 1} / {game?.total_questions || 20}</div>
                    <div className="font-heading text-2xl text-wta-accent">{score}</div>
                </div>
                {/* Timer */}
                <div className="h-1 bg-wta-surface mb-8 overflow-hidden rounded-full">
                    <div className="h-full bg-wta-primary transition-[width] duration-100 ease-linear" style={{ width: `${progress}%` }} />
                </div>
                {/* Category */}
                <div className="font-heading text-xs tracking-[0.4em] text-wta-accent uppercase">{current.category}</div>
                {/* Question */}
                <h2 className="font-heading text-3xl md:text-5xl tracking-tight uppercase mt-3 leading-tight">{current.question}</h2>
                {/* Options */}
                <div className="grid md:grid-cols-2 gap-3 mt-10">
                    {current.options.map((opt, i) => {
                        const correctness = feedback ? (i === feedback.correct_index ? "correct" : (feedback.correct === false && i === undefined ? "" : "")) : "";
                        const isUserSelectedWrong = feedback && !feedback.correct && i !== feedback.correct_index;
                        return (
                            <button
                                key={i}
                                disabled={answering || feedback}
                                onClick={() => onAnswer(i)}
                                data-testid={`trivia-option-${i}`}
                                className={`text-left px-5 py-4 border rounded-sm transition flex items-center gap-3 font-heading text-xl tracking-wide ${
                                    feedback && i === feedback.correct_index
                                        ? "bg-green-500/15 border-green-500 text-white"
                                        : feedback && !feedback.correct
                                            ? "border-wta-border opacity-60"
                                            : "bg-wta-surface border-wta-border hover:border-wta-primary"
                                }`}
                            >
                                <div className="w-7 h-7 border border-wta-border flex items-center justify-center text-sm rounded-sm">{String.fromCharCode(65 + i)}</div>
                                <div>{opt}</div>
                                {feedback && i === feedback.correct_index && <CheckCircle size={20} weight="fill" className="ml-auto text-green-500" />}
                            </button>
                        );
                    })}
                </div>
                {feedback && (
                    <div className="mt-6 text-center">
                        <div className={`font-heading text-2xl tracking-wide ${feedback.correct ? "text-green-400" : "text-wta-primary"}`}>
                            {feedback.correct ? `+${feedback.points} POINTS` : "WRONG ANSWER"}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
